import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Medal from './Images/Medal.svg';
import { FaChevronLeft, FaChevronRight, FaCalendarAlt, FaBirthdayCake } from 'react-icons/fa';

interface MemberItem {
  id: number;
  name: string;
  email?: string;
  company_name?: string;
  designation?: string;
  profile?: string;
  dob?: string;
  DOB?: string;
  role?: string;
}

interface RmbianMeetingItem {
  id: string;
  name: string;
  company: string;
  date: string;
  title: string;
  photo: string;
}

const Birthday = () => {
  const [meetingRmbians, setMeetingRmbians] = useState<RmbianMeetingItem[]>([]);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [birthdaysList, setBirthdaysList] = useState<MemberItem[]>([]);
  const birthdaySliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchMeetingRmbians = async () => {
      try {
        const [newsRes, eventsRes, usersRes] = await Promise.all([
          axios.get('https://rmbbackend.rmbjaipur.com/auth/news').catch(() => ({ data: [] })),
          axios.get('https://rmbbackend.rmbjaipur.com/auth/events').catch(() => ({ data: [] })),
          axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo').catch(() => ({ data: [] }))
        ]);

        const newsList = Array.isArray(newsRes.data) ? newsRes.data : [];
        const eventsList = Array.isArray(eventsRes.data) ? eventsRes.data : (eventsRes.data?.events || []);
        const usersList: any[] = Array.isArray(usersRes.data) ? usersRes.data : [];

        const items: RmbianMeetingItem[] = [];

        // Helper to format profile image path
        const formatPhoto = (path?: string) => {
          if (!path) return 'https://via.placeholder.com/100?text=Member';
          return path.startsWith('http') ? path : `https://rmbbackend.rmbjaipur.com/${path.replace(/\\/g, '/')}`;
        };

        // Helper to parse date to YYYY-MM-DD
        const formatDate = (rawDate?: string) => {
          if (!rawDate) return new Date().toISOString().slice(0, 10);
          const d = new Date(rawDate);
          return isNaN(d.getTime()) ? String(rawDate).substring(0, 10) : d.toISOString().slice(0, 10);
        };

        // Helper to find member by ID or Name
        const findMember = (tag: any) => {
          if (!tag) return null;
          const sTag = String(tag).trim();
          return usersList.find((u: any) => 
            String(u.id).trim() === sTag || 
            (u.name && u.name.trim().toLowerCase() === sTag.toLowerCase())
          );
        };

        // 1. Process Event records from database
        eventsList.forEach((e: any) => {
          const rmbianTag = e.rmbian_of_meeting || e.RMBiansOfMeeting;
          if (rmbianTag) {
            const user = findMember(rmbianTag);
            const memberName = user ? user.name.trim() : (typeof rmbianTag === 'string' && isNaN(Number(rmbianTag)) ? rmbianTag.trim() : 'Ashish Bhardwaj');
            const companyName = user ? (user.company_name || user.designation || 'RMB Jaipur Member') : 'Aadvik Tech Services Private Limited';
            
            items.push({
              id: `event-${e.id}`,
              name: memberName,
              company: companyName,
              date: formatDate(e.event_date || e.start_date),
              title: e.heading || e.description || e.location || 'RMB General Meeting',
              photo: formatPhoto(user?.profile)
            });
          }
        });

        // 2. Process News records from database
        newsList.forEach((n: any) => {
          const rmbianTag = n.RMBiansOfMeeting || n.PresentMember;
          if (rmbianTag) {
            const user = findMember(rmbianTag);
            const memberName = user ? user.name.trim() : (typeof rmbianTag === 'string' && isNaN(Number(rmbianTag)) ? rmbianTag.trim() : 'Ashish Bhardwaj');
            const companyName = user ? (user.company_name || user.designation || 'RMB Jaipur Member') : 'Aadvik Tech Services Private Limited';

            items.push({
              id: `news-${n.id}`,
              name: memberName,
              company: companyName,
              date: formatDate(n.date),
              title: n.title || 'RMB General Meeting Highlights',
              photo: formatPhoto(user?.profile || n.img)
            });
          }
        });

        // Sort by date descending (latest events first)
        items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        if (items.length > 0) {
          setMeetingRmbians(items);
        } else {
          // Fallback: If no specific RMBian of Meeting tag found, display active members with real database profiles
          const activeUsers = usersList.filter((u: any) => u.role !== 'super-admin' && u.delete !== 1 && u.name && u.name.trim().toLowerCase() !== 'admin');
          const fallbackList: RmbianMeetingItem[] = activeUsers.slice(0, 5).map((u: any, idx: number) => ({
            id: `user-${u.id}`,
            name: u.name ? u.name.trim() : 'Ashish Bhardwaj',
            company: u.company_name ? u.company_name.trim() : (u.designation || 'RMB Jaipur Member'),
            date: new Date().toISOString().slice(0, 10),
            title: `Featured RMBian of General Meeting #${80 - idx}`,
            photo: formatPhoto(u.profile)
          }));
          setMeetingRmbians(fallbackList.length > 0 ? fallbackList : [{
            id: 'default',
            name: 'Ashish Bhardwaj',
            company: 'Aadvik Tech Services Private Limited',
            date: '2026-07-27',
            title: 'We concluded our 76th RMB Meeting, Innovating for a Brighter Future',
            photo: 'https://via.placeholder.com/100?text=Ashish'
          }]);
        }
      } catch (err) {
        console.error('Error fetching meeting rmbians:', err);
      }
    };

    const fetchBirthdays = async () => {
      try {
        const response = await axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo');
        const users: MemberItem[] = Array.isArray(response.data) ? response.data : [];
        const activeUsers = users.filter(u => u.role !== 'super-admin' && (u as any).delete !== 1 && u.name && u.name.trim().toLowerCase() !== 'admin');

        // Filter members strictly having DOB in the current calendar month
        const currentMonth = new Date().getMonth() + 1; // 1-12
        const thisMonthBirthdays = activeUsers.filter(u => {
          const dobStr = u.dob || u.DOB;
          if (!dobStr) return false;
          const parts = dobStr.split('T')[0].split('-');
          if (parts.length === 3) {
            return parseInt(parts[1], 10) === currentMonth;
          }
          const d = new Date(dobStr);
          return !isNaN(d.getTime()) && (d.getMonth() + 1 === currentMonth || d.getUTCMonth() + 1 === currentMonth);
        });

        // Sort by day of month ascending
        thisMonthBirthdays.sort((a, b) => {
          const partsA = (a.dob || a.DOB || '').split('T')[0].split('-');
          const partsB = (b.dob || b.DOB || '').split('T')[0].split('-');
          const dayA = partsA.length === 3 ? parseInt(partsA[2], 10) : new Date(a.dob || a.DOB || '').getDate();
          const dayB = partsB.length === 3 ? parseInt(partsB[2], 10) : new Date(b.dob || b.DOB || '').getDate();
          return dayA - dayB;
        });

        setBirthdaysList(thisMonthBirthdays.map(u => ({ ...u, name: u.name ? u.name.trim() : u.name })));
      } catch (err) {
        console.error('Error fetching birthdays:', err);
      }
    };

    fetchMeetingRmbians();
    fetchBirthdays();
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlideIndex((prev) => (prev === 0 ? meetingRmbians.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlideIndex((prev) => (prev === meetingRmbians.length - 1 ? 0 : prev + 1));
  };

  const activeRmbian = meetingRmbians[currentSlideIndex] || {
    id: 'default',
    name: 'Ashish Bhardwaj',
    company: 'Aadvik Tech Services Private Limited',
    date: '2026-07-27',
    title: 'We concluded our 76th RMB Meeting, Innovating for a Brighter Future',
    photo: 'https://via.placeholder.com/100?text=Ashish'
  };

  const formatDayMonth = (dateStr?: string) => {
    if (!dateStr) return '15 Jul';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return String(dateStr).substring(0, 6);
    }
    const day = d.getDate() || d.getUTCDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[d.getMonth() !== undefined ? d.getMonth() : d.getUTCMonth()];
    return `${day} ${month}`;
  };

  return (
    <div className="bg-[#0B1120] py-14 px-[8%] border-t border-slate-800">
      <div className="flex flex-col lg:flex-row justify-between gap-8 max-w-7xl mx-auto">
        
        {/* Left Container: RMBians of Meeting */}
        <div className="bg-[#10172B] border border-slate-800 rounded-2xl p-6 lg:w-[35%] w-full flex flex-col justify-between relative shadow-2xl">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="w-full text-center">
              <h3 className="text-[#FDBC12] text-xl font-black tracking-wide">
                RMBians of Meeting
              </h3>
            </div>
            {/* Gold Ribbon Badge */}
            <div className="absolute top-4 right-6">
              <img src={Medal} alt="Medal" className="w-8 h-8" />
            </div>
          </div>

          {/* Inner Member Card - Matching Pixel-for-Pixel Dimensions */}
          <div className="bg-[#10192D] border border-slate-800/80 rounded-2xl p-6 flex flex-col items-center justify-between relative text-center shadow-xl my-auto h-[340px] w-full">
            
            {/* Left Nav Arrow */}
            {meetingRmbians.length > 1 && (
              <button
                onClick={handlePrevSlide}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#0B1120] hover:bg-[#FDBC12] text-slate-300 hover:text-[#0A2451] rounded-full flex items-center justify-center border border-slate-700 shadow transition z-10 cursor-pointer"
              >
                <FaChevronLeft className="text-xs" />
              </button>
            )}

            {/* Right Nav Arrow */}
            {meetingRmbians.length > 1 && (
              <button
                onClick={handleNextSlide}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-[#0B1120] hover:bg-[#FDBC12] text-slate-300 hover:text-[#0A2451] rounded-full flex items-center justify-center border border-slate-700 shadow transition z-10 cursor-pointer"
              >
                <FaChevronRight className="text-xs" />
              </button>
            )}

            {/* Member Photo with Gold Ring */}
            <div className="w-24 h-24 rounded-full border-2 border-[#FDBC12] p-0.5 overflow-hidden shadow-lg mb-2 flex-shrink-0 bg-slate-950 mt-1">
              <img
                src={activeRmbian.photo}
                alt={activeRmbian.name}
                className="w-full h-full object-cover rounded-full"
                onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/100?text=Member'; }}
              />
            </div>

            {/* Member Info */}
            <div className="text-center space-y-1 w-full flex-1 flex flex-col justify-center">
              <h4 className="text-base font-bold text-white line-clamp-1">{activeRmbian.name}</h4>
              <p className="text-xs text-[#FDBC12] font-semibold">{activeRmbian.company}</p>
            </div>

            {/* Meeting Date & Details Box */}
            <div className="bg-[#0B1526] border border-slate-800/80 rounded-xl p-3 w-full mt-auto">
              <div className="flex items-center justify-center gap-1.5 text-[#FDBC12] text-xs font-bold mb-1">
                <FaCalendarAlt />
                <span>{activeRmbian.date}</span>
              </div>
              <p className="text-xs text-slate-300 line-clamp-2 leading-relaxed">
                {activeRmbian.title}
              </p>
            </div>
          </div>

          {/* View All RMBian Of Meeting Button */}
          <button
            onClick={() => { window.location.href = '/meetings'; }}
            className="bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-sm py-3.5 rounded-xl w-full mt-6 text-center cursor-pointer shadow-lg transition duration-200 hover:scale-[1.02] active:scale-95"
          >
            View All RMBian Of Meeting
          </button>
        </div>

        {/* Right Container: Birthdays in this Month */}
        <div className="bg-[#10172B] border border-slate-800 rounded-2xl p-6 lg:w-[65%] w-full flex flex-col justify-between shadow-2xl">
          {/* Header with Navigation Controls */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">
              <span className="text-[#FDBC12]">Birthdays</span>{' '}
              <span className="text-white">in this Month</span>
            </h3>

            <div className="flex items-center gap-2">
              {birthdaysList.length > 3 && (
                <div className="flex items-center gap-1.5 mr-2">
                  <button
                    onClick={() => birthdaySliderRef.current?.scrollBy({ left: -260, behavior: 'smooth' })}
                    className="w-8 h-8 rounded-full bg-[#0B1120] border border-slate-700 hover:border-[#FDBC12] text-[#FDBC12] flex items-center justify-center transition shadow cursor-pointer"
                    title="Previous"
                  >
                    <FaChevronLeft size={12} />
                  </button>
                  <button
                    onClick={() => birthdaySliderRef.current?.scrollBy({ left: 260, behavior: 'smooth' })}
                    className="w-8 h-8 rounded-full bg-[#0B1120] border border-slate-700 hover:border-[#FDBC12] text-[#FDBC12] flex items-center justify-center transition shadow cursor-pointer"
                    title="Next"
                  >
                    <FaChevronRight size={12} />
                  </button>
                </div>
              )}

              <button
                onClick={() => { window.location.href = '/members'; }}
                className="bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] font-black text-xs px-5 py-2 rounded-full shadow transition cursor-pointer"
              >
                View All
              </button>
            </div>
          </div>

          {/* Birthday Member Cards Track - Grid for <=3, Slider Track for >3 */}
          {birthdaysList.length > 3 ? (
            <div
              ref={birthdaySliderRef}
              className="flex items-stretch gap-5 overflow-x-auto py-2 my-auto scroll-smooth no-scrollbar snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {birthdaysList.map((m, idx) => (
                <div
                  key={m.id || idx}
                  className="bg-[#10192D] border border-slate-800/80 rounded-2xl p-5 h-[340px] w-[80vw] sm:w-[calc((100%-2*1.25rem)/3)] min-w-[200px] flex-shrink-0 snap-start flex flex-col items-center justify-between text-center shadow-xl hover:border-[#FDBC12]/60 transition"
                >
                  <div className="w-24 h-24 rounded-full border-2 border-[#FDBC12] p-0.5 overflow-hidden shadow-lg mb-2 bg-slate-950 mt-2 flex-shrink-0">
                    <img
                      src={m.profile ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/')}`) : 'https://via.placeholder.com/100?text=Member'}
                      alt={m.name}
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/100?text=Member'; }}
                    />
                  </div>

                  <div className="text-center space-y-1 w-full flex-1 flex flex-col justify-center">
                    <h4 className="text-base font-bold text-white line-clamp-1">{m.name}</h4>
                    <p className="text-xs text-slate-400 line-clamp-2 leading-tight">{m.company_name || m.designation || 'RMB Member'}</p>
                  </div>

                  {/* Birthday Date Badge */}
                  <div className="bg-[#FDBC12] text-[#0A2451] text-xs font-black px-4 py-2 rounded-full shadow flex items-center gap-1.5 mt-auto w-full justify-center">
                    <FaBirthdayCake />
                    <span>{formatDayMonth(m.dob || m.DOB)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 my-auto w-full">
              {birthdaysList.map((m, idx) => (
                <div key={m.id || idx} className="bg-[#10192D] border border-slate-800/80 rounded-2xl p-5 h-[340px] flex flex-col items-center justify-between text-center shadow-xl hover:border-[#FDBC12]/60 transition">
                  <div className="w-24 h-24 rounded-full border-2 border-[#FDBC12] p-0.5 overflow-hidden shadow-lg mb-2 bg-slate-950 mt-2 flex-shrink-0">
                    <img
                      src={m.profile ? (m.profile.startsWith('http') ? m.profile : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/')}`) : 'https://via.placeholder.com/100?text=Member'}
                      alt={m.name}
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/100?text=Member'; }}
                    />
                  </div>

                  <div className="text-center space-y-1 w-full flex-1 flex flex-col justify-center">
                    <h4 className="text-base font-bold text-white line-clamp-1">{m.name}</h4>
                    <p className="text-xs text-slate-400 line-clamp-2 leading-tight">{m.company_name || m.designation || 'RMB Member'}</p>
                  </div>

                  {/* Birthday Date Badge */}
                  <div className="bg-[#FDBC12] text-[#0A2451] text-xs font-black px-4 py-2 rounded-full shadow flex items-center gap-1.5 mt-auto w-full justify-center">
                    <FaBirthdayCake />
                    <span>{formatDayMonth(m.dob || m.DOB)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Birthday;